import * as React from "react";
import _ from "lodash";

import { eIconType, SettingsType, ItemsMgmtDialogInfo } from '@Primitive/SettingsListType';
import { Divider } from 'material-ui';
import { Dropdown, Whisper, Popover } from 'rsuite';

import { isDefined, IsNullOrWhiteSpace, getRandomSmallKey, IsNullOrEmpty } from '@Utils/Micro';
import { popupStaticWindow } from '@Utils/Utils';
import { IAdvancedTippy, AdvancedTippy } from '@Primitive/AdvancedTippy';

import "./ToolbarIconButton.less";
// ================================================================================================
export type TooltipPosition = "top" | "bottom" | "left" | "right";
export interface ToolbarIconButtonProps extends React.ImgHTMLAttributes<HTMLElement> {
    tooltip?: string;
    tooltipPosition?: TooltipPosition;
    showBubble?: boolean;
    bubbleContent?: string;
    bubbleBackgroundColor?: string;
    bubbleColor?: string;
    bubbleFontSize?: number;
    bubbleOtherStyles?: any;
    iconType?: eIconType;
    selected?: boolean;
    onIconClickDropdown?: boolean;
    inMenuList?: boolean;
    advancedPopup?: IAdvancedTippy;
    advancedTooltip?: IAdvancedTippy;
    buttonPressed?: (element: any, data: any) => void;
    elementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void;
    isHighlighted?: boolean;
}
// ------------------------------------------------------------------------------------------------
export function ToolbarIconButton(allProps: ToolbarIconButtonProps) {
    let {
        iconType,
        tooltip,
        tooltipPosition,
        selected,
        className,
        showBubble,
        bubbleBackgroundColor,
        bubbleContent,
        bubbleColor,
        bubbleFontSize,
        bubbleOtherStyles,
        advancedTooltip,
        advancedPopup,
        buttonPressed,
        elementChanged,
        inMenuList,
        isHighlighted,
        ...props
    } = allProps;

    let bubbleJSX = (showBubble && !IsNullOrEmpty(bubbleContent)) ? <span style={{
        position: 'absolute',
        zIndex: 1,

        transform: 'translate(50%, -50%)',

        /* Center the content */
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '100%',
        backgroundColor: bubbleBackgroundColor,
        color: bubbleColor,

        fontSize: bubbleFontSize,
        textAlign: 'center',

        ...bubbleOtherStyles
    }}>
        {bubbleContent}
    </span> : null;

    // set custom class if selected
    if (selected) {
        if (!isDefined(className))
            className = 'selected';
        else
            className += " selected";
    }

    if (isHighlighted ?? true) {
        className += ' highlighted';
    }

    const jsx = !isDefined(iconType) || iconType == eIconType.fontAwesome ?
        <div style={{ position: (inMenuList) ? 'unset' : 'relative' }}>
            {bubbleJSX}
            {isDefined(className) ?
                <i id="icons" className={"toolbar-icon-button " + className}   {...props} />
                : <div id="icons" {...props} />}
        </div>
        :
        <div id="icons"
            style={{ position: (inMenuList) ? 'unset' : 'relative' }}
            className={"toolbar-icon-button " + className}
            onClick={props.onClick}
        >
            {bubbleJSX}
            <img
                className='toolbar-icon-button-img'
                {...props}
                onClick={undefined}
            />
        </div>;

    if (IsNullOrWhiteSpace(tooltip)
        && !isDefined(advancedTooltip)
        && !isDefined(advancedPopup))
        return jsx;

    let placement: TooltipPosition = "bottom";
    if (isDefined(tooltipPosition))
        placement = tooltipPosition;

    return (
        <AdvancedTippy
            placement={placement}
            tooltip={tooltip}
            advancedTooltip={advancedTooltip}
            advancedPopup={advancedPopup}
            iconClicked={props.onClick}
            buttonPressed={buttonPressed}
            elementChanged={elementChanged}>
            {jsx}
        </AdvancedTippy>
    );
}
// ------------------------------------------------------------------------------------------------
export interface ToolbarIconButtonSeparatorProps extends React.HTMLAttributes<HTMLElement> { }
export function ToolbarIconButtonSeparator(props: ToolbarIconButtonSeparatorProps) {
    return <span id="separators" {...props} />;
}
// ================================================================================================
/**
 * Information about toolbar icons.
 */
export interface ToolbarIconInfo {
    /**
     * Define button type
     */
    type?: 'text' | 'icon' | 'image';

    /**
     * If true, all other fields are ignored
     */
    isSeparator?: boolean;

    /**
     * Support text based toolbars icons
     */
    label?: string;

    /**
     * Icon's tooltip
     */
    tooltip?: string;

    /**
     * Icon's unique ID
     */
    id?: string;

    /**
     * Icon
     */
    icon?: string;

    /**
     * Icon type
     */
    iconType?: eIconType;

    /**
     * Optional color when icon type is font-awesome.
     */
    iconColor?: string;

    /**
     * Enable Bubble
     */
    showBubble?: boolean;

    /**
     * Styling Settings for the Bubble
     */
    bubbleBackgroundColor?: string;
    bubbleColor?: string;
    bubbleFontSize?: number;

    /**
     *  Bubble Content
     */
    bubbleContent?: string;

    /**
     * Whether to set as selected upon user click or just send the port event.
     */
    autoSelect?: boolean;

    /**
     * nested toolbar icons.
     */
    nestedItems?: ToolbarIconInfo[];
    nestedItemsPlacement?:
    | 'bottomStart'
    | 'bottomEnd'
    | 'topStart'
    | 'topEnd'
    | 'leftStart'
    | 'rightStart'
    | 'leftEnd'
    | 'rightEnd'

    /**
     * right Icon (mostly used for nested menu)
     */
    rightIcon?: string;


    /**
    *  on Icon click show also dropdown
    */
    onIconClickDropdown?: boolean;

    /**
     * Default is true
     */
    isClickable?: boolean;

    /**
     * Styles for buttons/strings/icons
     */
    style?: React.CSSProperties;

    /**
     * Set external page url
     */
    externalPageUrl?: string;

    // dynamic form inside tooltip/popup
    advancedTooltip?: IAdvancedTippy;
    advancedPopup?: IAdvancedTippy;

    // extends with priv data
    priv?: any;

    isHighlighted?: boolean;
}
// --------------------------------------------------------------------------------------------
export function ToolbarIconInfo_GetSettingList(hideAutoSelectable: boolean = false, enableTableDropDown: boolean = false): ItemsMgmtDialogInfo<any, ToolbarIconInfo>[] {
    return _.compact([
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: "Separator",
                dataPath: "isSeparator"
            },
        (enableTableDropDown) ? null :
            (hideAutoSelectable) ?
                {
                    type: SettingsType.Boolean,
                    label: "Auto selectable",
                    dataPath: "autoSelect",
                    isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                } : null,
        {
            type: SettingsType.String,
            label: "ID",
            dataPath: "id",
            isUnique: true,
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        {
            type: SettingsType.DropDown,
            label: "Type",
            dataPath: "type",
            items: [
                { label: "Text", value: "text" },
                { label: "Icon", value: "icon" }
            ]
        },
        {
            type: SettingsType.String,
            label: "Label",
            dataPath: "label",
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.String,
                label: "Tooltip",
                dataPath: "tooltip",
                isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: "Is Dropdown",
                dataPath: "onIconClickDropdown"
            },
        {
            type: SettingsType.ExtendedIcon,
            label: "Icon",
            dataPath: "icon",
            iconTypePath: "iconType",
            supportUpload: { ext: 'png', imgQuality: 0.9, maxHeight: 64, maxWidth: 64 },
            supportUrl: true,
            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
        },
        {
            type: SettingsType.ColorPicker,
            label: "Icon color",
            dataPath: "iconColor",
            isEnabled: (data, row: ToolbarIconInfo) => row.iconType == eIconType.fontAwesome
        },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Boolean,
                label: 'Show Bubble',
                dataPath: 'showBubble',
                tooltip: 'If enabled, show the bubble indicator'

            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.ColorPicker,
                label: 'Bubble Text Color',
                tooltip: 'Color for the bubble text',
                dataPath: 'bubbleColor',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :

            {
                type: SettingsType.ColorPicker,
                label: 'Bubble Background Color',
                tooltip: 'Color of bubble background',
                dataPath: 'bubbleBackgroundColor',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.String,
                label: "Bubble Content",
                tooltip: 'Set Bubble Content.',
                dataPath: "bubbleContent",
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble
            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.Slider,
                label: 'Bubble Font size',
                tooltip: 'Configure bubble size.',
                dataPath: 'bubbleFontSize',
                minValue: 9,
                maxValue: 12,
                stepValue: 1,
                valuePosfix: 'px',
                isEnabled: (data, row: ToolbarIconInfo) => row.showBubble

            },
        (enableTableDropDown) ? null :
            {
                type: SettingsType.ItemsMgmtDialog,
                label: "Nested Buttons",
                dataPath: "nestedItems",
                isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator,
                itemsInfo: _.compact([
                    {
                        type: SettingsType.Boolean,
                        label: "Separator",
                        dataPath: "isSeparator"
                    },
                    (hideAutoSelectable) ?
                        {
                            type: SettingsType.Boolean,
                            label: "Auto selectable",
                            dataPath: "autoSelect",
                            isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                        } : null,
                    {
                        type: SettingsType.String,
                        label: "ID",
                        dataPath: "id",
                        isUnique: true,
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.String,
                        label: "Label",
                        dataPath: "label",
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.String,
                        label: "Tooltip",
                        dataPath: "tooltip",
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.ExtendedIcon,
                        label: "Icon",
                        dataPath: "icon",
                        iconTypePath: "iconType",
                        supportUpload: { ext: 'png', imgQuality: 0.9, maxHeight: 64, maxWidth: 64 },
                        supportUrl: true,
                        isHiddenCell: (data, row: ToolbarIconInfo) => row.isSeparator
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Icon color",
                        dataPath: "iconColor",
                        isEnabled: (data, row: ToolbarIconInfo) => row.iconType == eIconType.fontAwesome
                    }
                ]),
                createNewItem: () => {
                    return {
                        icon: "",
                        tooltip: "Icon tooltip",
                        onIconClickDropdown: false,
                        iconColor: "gray",
                        iconType: eIconType.fontAwesome,
                        showBubble: false,
                        bubbleBackgroundColor: '#D0021B',
                        bubbleColor: 'white',
                        bubbleFontSize: 15,
                        bubbleContent: '1',
                        id: "Icon " + getRandomSmallKey()
                    } as ToolbarIconInfo;
                }
            }
    ]);
}
// --------------------------------------------------------------------------------------------
function getProps(iconInfo: ToolbarIconInfo, style: React.CSSProperties): ToolbarIconButtonProps {
    const iconProps: ToolbarIconButtonProps = {
        iconType: iconInfo.iconType,
        showBubble: iconInfo.showBubble,
        bubbleBackgroundColor: iconInfo.bubbleBackgroundColor,
        bubbleColor: iconInfo.bubbleColor,
        bubbleFontSize: iconInfo.bubbleFontSize,
        bubbleContent: iconInfo.bubbleContent,
        style: {
            ...style,
            ...(iconInfo.isClickable == false ? {
                cursor: 'unset',
                boxShadow: 'unset'
            } : {}),
            color: iconInfo.isClickable == false && !isDefined(iconInfo.iconColor)
                ? 'unset'
                : iconInfo.iconColor,
            ...iconInfo.style
        }
    };

    if (iconInfo.iconType == eIconType.fontAwesome)
        iconProps.className = iconInfo.icon;
    else {
        iconProps.src = iconInfo.icon;
        if (_.has(style, "height"))
            iconProps.height = style.height;
    }

    return iconProps;
};
//---------------------------------------------------------------------------------------------
function _renderNestedItemsBody(item: ToolbarIconInfo, i: number, hasNestedMenu: boolean, styleToolbarIcons?: any) {
    let { style, ...otherItemProps } = getProps(item, styleToolbarIcons);

    let labelStyles: React.CSSProperties = {
        margin: 12,
        fontSize: 14,
        width: '100%',
        color: item?.iconColor
    };

    let label = <div
        key={`label-${item.id}-${i}`}
        style={labelStyles}>
        {item?.label ?? item?.tooltip}
    </div>;

    let rightIcon = hasNestedMenu ? <div
        key={`right-icon-${item.id}-${i}`}
        style={{ position: 'unset' }}>
        <i className={item?.rightIcon ?? "fas fa-angle-right"}
            style={{
                fontSize: 16,
                lineHeight: '24px',
                textAlign: 'center',
                ...style,
                height: 24,
                width: 24
            }}>
        </i>
    </div> : null;

    let leftIcon = <ToolbarIconButton
        id="icons"
        key={`left-icon-${item.id}-${i}`}
        style={{
            fontSize: 16,
            lineHeight: '24px',
            textAlign: 'center',
            ...style,
            height: 24,
            width: 24
        }}
        inMenuList={true}
        {...otherItemProps}
    />;

    return <div style={{
        display: "flex",
        alignItems: "center",
        width: 'inherit'
    }}>
        {leftIcon}
        {label}
        {rightIcon}
    </div>;
}
//---------------------------------------------------------------------------------------------
function _onClickHandle(item: ToolbarIconInfo, onIconClick: (iconInfo: ToolbarIconInfo) => void) {
    if (item.isClickable != false) {
        onIconClick(item);
        if (!IsNullOrWhiteSpace(item.externalPageUrl)) {
            popupStaticWindow(item.externalPageUrl, true);
        }
    }
}
//---------------------------------------------------------------------------------------------
function _createNestedMenuItems(menuItem: ToolbarIconInfo, i: number, onIconClick: (iconInfo: ToolbarIconInfo) => void, closeFirstLevelDropdown: () => any, styleToolbarIcons: React.CSSProperties = {}) {
    return <Dropdown
        key={5}
        trigger={['hover', 'click']}
        placement={menuItem.nestedItemsPlacement ?? 'rightStart'}
        noCaret
        renderTitle={children => _renderNestedItemsBody(menuItem, i, true, styleToolbarIcons)}>
        {
            _.map(menuItem.nestedItems, (item, index) => {
                if (item.isSeparator)
                    return <Divider key={`separator-${item.id}-${index}`} />

                let hasNestedMenu = _.isArray(item?.nestedItems) ? true : false;
                let onSelect = (eventKey: any) => {
                    if (!hasNestedMenu)
                        _onClickHandle(item, onIconClick);
                    closeFirstLevelDropdown();
                }

                return <Dropdown.Item id="dropdown-item"
                    onSelect={onSelect}
                    eventKey={`inner-dropdown-item-${item.id}`}
                    key={`inner-dropdown-item-${item.id}-${index}`}
                    icon={<i style={{ fontSize: '25px' }} className={item.icon}></i>}
                >
                    {hasNestedMenu
                        ? _createNestedMenuItems(item, index, onIconClick, closeFirstLevelDropdown, styleToolbarIcons)
                        : _renderNestedItemsBody(item, index, false, styleToolbarIcons)}
                </Dropdown.Item>
            })
        }
    </Dropdown>;

}
// --------------------------------------------------------------------------------------------
function renderNestedItems(item: ToolbarIconInfo,
    i: number,
    onIconClick: (iconInfo: ToolbarIconInfo) => void,
    styleToolbarIcons: React.CSSProperties = {},
    closeFirstLevelDropdown: () => any) {

    if (item.isSeparator)
        return <Divider key={'separator-' + item.id + i} />

    let hasNestedMenu = item?.nestedItems?.length > 0 ? true : false;
    let onSelect = undefined;
    if (!hasNestedMenu)
        onSelect = (eventkey: any) => {
            _onClickHandle(item, onIconClick);

            //Close First level Dropdown (Whisper)
            closeFirstLevelDropdown();
        }

    {/**1st Level Dropdown*/ }
    return <Dropdown.Item
        key={`outer-dropdown-item-${i}`}
        id="dropdown-item"
        onSelect={onSelect}
        eventKey={item.id}
        icon={<i style={{ fontSize: '25px' }} className={item.icon}></i>}
        style={{ color: item?.iconColor }}>
        {hasNestedMenu
            ? _createNestedMenuItems(item, i, onIconClick, closeFirstLevelDropdown, styleToolbarIcons)
            : _renderNestedItemsBody(item, i, false, styleToolbarIcons)}
    </Dropdown.Item>
}

// --------------------------------------------------------------------------------------------
export function ToolbarIconInfo_ButtonRender(
    toolbarIcons: ToolbarIconInfo[],
    hiddenToolbarIcons: string[],
    selectedToolbarIcons: string[],
    styleToolbarIcons: React.CSSProperties = {},
    onIconClick: (iconInfo: ToolbarIconInfo) => void,
    renderFrom?: string,
    buttonPressed?: (element: any, data: any) => void,
    elementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void) {

    let fromCharts = renderFrom == 'charts';
    let bubbleStyle: React.CSSProperties = {
        height: fromCharts ? 12 : 14,
        width: fromCharts ? 12 : 16,
        top: fromCharts ? 4 : 10,
        right: 10
    };

    return _(toolbarIcons)
        .filter(x => !_.includes(hiddenToolbarIcons, x.id))
        .map((iconInfo, index) => {

            let isSeparator = iconInfo.isSeparator;
            if (isSeparator) {
                return [
                    <ToolbarIconButtonSeparator
                        key={'separator-' + iconInfo.id + index}
                        style={{
                            backgroundColor: iconInfo.iconColor,
                            ...styleToolbarIcons
                        }} />
                ];
            }

            // inner items
            let nestedItems = _.filter(iconInfo.nestedItems, icon =>
                icon != undefined && !_.includes(hiddenToolbarIcons, icon.id));
            let hasNestedItems = _.isArray(nestedItems) && nestedItems.length > 0;
            let isImageOrText = iconInfo.type == 'image' || iconInfo.type == 'text';

            // open/close inner
            let triggerRef: any = React.createRef();
            const closeMenu = () => {
                if (isDefined(triggerRef)) {
                    if (isDefined(triggerRef.current))
                        triggerRef.current.close();
                    else
                        triggerRef.close();
                }
            };
            const openMenu = () => {
                if (isDefined(triggerRef)) {
                    if (isDefined(triggerRef.current))
                        triggerRef.current.open();
                    else
                        triggerRef.open();
                }
            };

            // click callback
            let onClick = _.noop;
            if (iconInfo.isClickable != false) {
                onClick = () => {
                    onIconClick(iconInfo);

                    if (!IsNullOrWhiteSpace(iconInfo.externalPageUrl))
                        popupStaticWindow(iconInfo.externalPageUrl, true);

                    // open manually (prevented from tippy)
                    if (isImageOrText && hasNestedItems && iconInfo.onIconClickDropdown)
                        openMenu();
                };
            }

            let domNode: React.ReactNode = null;
            if (isImageOrText) {
                let style: React.CSSProperties = {
                    width: 'auto',
                    ...styleToolbarIcons,
                    ...(iconInfo.isClickable == false ? {
                        cursor: 'unset',
                        boxShadow: 'unset'
                    } : {}),
                    color: iconInfo.isClickable == false && !isDefined(iconInfo.iconColor)
                        ? 'unset'
                        : iconInfo.iconColor,
                    ...iconInfo.style
                };

                let className = 'toolbar-icon-text';
                if (_.includes(selectedToolbarIcons, iconInfo.id))
                    className += ' selected';

                if (iconInfo.type == 'image')
                    domNode = <img
                        id={iconInfo.isClickable == false ? '' : 'icons'}
                        className={className}
                        style={style}
                        src={iconInfo.icon}
                        onClick={onClick}>
                    </img>;
                else if (iconInfo.type == 'text')
                    domNode = <div
                        id='icons'
                        className={className}
                        style={style}
                        onClick={onClick}>
                        {iconInfo.label}
                    </div>;

                if (IsNullOrWhiteSpace(iconInfo.tooltip)
                    && !isDefined(iconInfo.advancedTooltip)
                    && !isDefined(iconInfo.advancedPopup)
                    && !hasNestedItems)
                    return domNode;

                domNode = (
                    <AdvancedTippy
                        placement='bottom'
                        tooltip={iconInfo.tooltip}
                        advancedTooltip={iconInfo.advancedTooltip}
                        advancedPopup={iconInfo.advancedPopup}
                        iconClicked={onClick}
                        buttonPressed={buttonPressed}
                        elementChanged={elementChanged}>
                        {domNode}
                    </AdvancedTippy>
                );
                if (!hasNestedItems)
                    return domNode;
            } else {
                domNode = (
                    <ToolbarIconButton
                        key={`icon-${iconInfo.id}-${index}`}
                        isHighlighted={iconInfo.isHighlighted}
                        selected={_.includes(selectedToolbarIcons, iconInfo.id)}
                        tooltip={iconInfo.tooltip}
                        bubbleOtherStyles={bubbleStyle}
                        advancedTooltip={iconInfo.advancedTooltip}
                        advancedPopup={iconInfo.advancedPopup}
                        buttonPressed={buttonPressed}
                        elementChanged={elementChanged}
                        onClick={onClick}
                        {...getProps(iconInfo, styleToolbarIcons)} />
                );
            }

            if (hasNestedItems && iconInfo.onIconClickDropdown) {
                return [
                    <Whisper
                        ref={(ref: any) => triggerRef = ref}
                        key={`wisper-icon-${iconInfo.id}-${index}`}
                        trigger='click'
                        placement='bottomStart'
                        preventOverflow
                        speaker={<Popover
                            full
                            classPrefix='popover'
                            style={{
                                border: 'none',
                                zIndex: 20000
                            }}>
                            <Dropdown.Menu style={{ backgroundColor: 'inherit', border: 'none' }}>
                                {_.map(nestedItems, (item, i) =>
                                    renderNestedItems(item, i, onIconClick, styleToolbarIcons, closeMenu))}
                            </Dropdown.Menu>
                        </Popover>}>
                        {domNode}
                    </Whisper>
                ];
            } else {
                let nodes = [domNode];
                if (hasNestedItems && (iconInfo.onIconClickDropdown === false || iconInfo.onIconClickDropdown == null)) {
                    nodes.push(
                        <Whisper
                            ref={triggerRef}
                            key={`wisper-icon-${iconInfo.id}-${index}`}
                            placement='bottomStart'
                            trigger='click'
                            preventOverflow
                            speaker={<Popover
                                classPrefix='popover'
                                style={{
                                    border: 'none',
                                    zIndex: 20000
                                }}>
                                <Dropdown.Menu style={{ backgroundColor: 'inherit', border: 'none' }}>
                                    {_.map(nestedItems, (item, i) =>
                                        renderNestedItems(item, i, onIconClick, styleToolbarIcons, closeMenu))}
                                </Dropdown.Menu>
                            </Popover>}>
                            <ToolbarIconButton
                                key={`dropdown-toggle-${iconInfo.id}-${index}`}
                                className='fas fa-caret-down' />
                        </Whisper>
                    );
                }
                return nodes;
            }
        })
        .flatMap()
        .value();
}